import { Button, Chip, Stack, Typography } from "@mui/material";
import { useSettlementReportsQuery } from "Api/Queries/Settlements/useSettlementReportsQuery";
import { Colors } from "Components/Layout/Themes/Colors";
import { ErrorText } from "Components/Shared/Errors/ErrorText";
import { PrimaryPaper } from "Components/Shared/PrimaryPaper";
import { useLoggedInUser } from "Hooks/useLoggedInUser";
import { SettlementFilter, SettlementStatus } from "Infrastructure/Api/Api";
import { useNavigate } from "Navigation/useNavigate";
import { toast } from "sonner";
import { formatCurrency, getRegionCurrency } from "Utils/CurrencyUtils";
import { appUrl } from "Utils/UrlUtils";
import { Resources, useResource } from "Translations/Resources";

const COMPONENT_RESOURCES = Resources.Components.UnpaidSettlementPopup;
const SETTLEMENT_STATUS_RESOURCES =
  Resources.Components.Settlements.SettlementStatus;
const SETTLEMENT_LIST_RESOURCES =
  Resources.Components.Settlements.SettlementList;

export const UnpaidSettlementPopup = () => {
  const navigate = useNavigate();
  const currentUser = useLoggedInUser();
  const { t } = useResource();

  const { data: settlementData, isLoading } = useSettlementReportsQuery(
    currentUser?.partyPublicID,
    [SettlementFilter.Unpaid, SettlementFilter.Overdue],
  );

  const firstSettlement = settlementData?.settlements?.[0];

  if (isLoading || !firstSettlement) {
    return null;
  }

  const hasMultipleSettlements = settlementData.settlements.length > 1;
  const isSingleOverdue =
    !hasMultipleSettlements &&
    firstSettlement.settlementStatus === SettlementStatus.Overdue;

  // A merchant's settlements are always denominated in their own market's currency.
  const settlementText = hasMultipleSettlements
    ? t(COMPONENT_RESOURCES.PayMonthlySettlements)
    : t(COMPONENT_RESOURCES.PayMonthlySettlement, {
        amount: formatCurrency({
          amount: firstSettlement.amount,
          currencyCode: currentUser?.region
            ? getRegionCurrency(currentUser.region)
            : undefined,
        }),
      });

  const settlementTypeText =
    firstSettlement.type === "HunterCommissionFunding"
      ? t(SETTLEMENT_LIST_RESOURCES.SettlementTypeHunterCommissionFunding)
      : t(SETTLEMENT_LIST_RESOURCES.SettlementTypeDefault);

  const handlePaymentButtonClick = () => {
    if (hasMultipleSettlements) {
      return navigate(appUrl("merchant-settlements"));
    }

    const transactionPublicID = firstSettlement.transaction?.publicID;
    if (transactionPublicID) {
      return navigate(
        `${appUrl("customer-payment-pay")}/${transactionPublicID}`,
      );
    }

    toast.error(t(COMPONENT_RESOURCES.NoSettlementTransactionError));
  };

  return (
    <PrimaryPaper sx={{ border: `1px solid ${Colors.Red}` }}>
      <Stack alignItems="center" justifyContent="center" gap={1}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="center"
          gap={2}
        >
          <ErrorText>{settlementText}</ErrorText>
          {isSingleOverdue && (
            <Chip
              label={t(SETTLEMENT_STATUS_RESOURCES.Overdue)}
              color="error"
              size="small"
            />
          )}
        </Stack>
        {!hasMultipleSettlements && (
          <Typography variant="body2" color="text.secondary">
            {settlementTypeText}
          </Typography>
        )}
      </Stack>
      <Stack gap={5} mt={3}>
        <Button
          fullWidth
          onClick={handlePaymentButtonClick}
          color="primary"
          variant="contained"
          sx={{
            "&.Mui-disabled": {
              color: ({ palette }) => palette.common.white,
              background: ({ palette }) => palette.action.disabledBackground,
            },
            height: (theme) => theme.spacing(13),
          }}
        >
          <Typography fontWeight={600} fontSize={14}>
            {t(COMPONENT_RESOURCES.PayMonthlySettlements)}
          </Typography>
        </Button>
      </Stack>
    </PrimaryPaper>
  );
};
